import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";

export type ProductInfo = {
  id: string;
  name: string;
  image: string;
  alt: string;
  status: string;
  detail: string;
  description: string;
};

export type PanelState =
  | { kind: "configure" }
  | { kind: "product"; product: ProductInfo }
  | { kind: "ownership" }
  | { kind: "interest"; source: string; build?: string }
  | null;

type ExperiencePanelProps = {
  panel: PanelState;
  onClose: () => void;
  onRequestInterest: (source: string, build?: string) => void;
};

function Arrow() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="arrow">
      <path d="M3 10h13M11 5l5 5-5 5" />
    </svg>
  );
}

function Configurator({ onRequestInterest }: Pick<ExperiencePanelProps, "onRequestInterest">) {
  const storedBuild = useMemo(() => {
    try {
      const value = JSON.parse(localStorage.getItem("vrts:last-build") ?? "null") as {
        finish?: string;
        suppressed?: boolean;
      } | null;
      if (value?.finish !== "carbon" && value?.finish !== "field-earth") return null;
      if (typeof value.suppressed !== "boolean") return null;
      return value as { finish: "carbon" | "field-earth"; suppressed: boolean };
    } catch {
      return null;
    }
  }, []);
  const [finish, setFinish] = useState<"carbon" | "field-earth">(storedBuild?.finish ?? "carbon");
  const [suppressed, setSuppressed] = useState(storedBuild?.suppressed ?? false);
  const [saved, setSaved] = useState(Boolean(storedBuild));

  const build = useMemo(() => {
    const finishCode = finish === "carbon" ? "CBN" : "FDE";
    return `R1-${finishCode}-${suppressed ? "SUP" : "RFL"}`;
  }, [finish, suppressed]);

  const image = suppressed
    ? "/media/r1-suppressed.webp"
    : finish === "field-earth"
      ? "/media/r1-field-earth.webp"
      : "/media/r1-hero-pair.webp";

  const saveBuild = () => {
    localStorage.setItem(
      "vrts:last-build",
      JSON.stringify({ finish, suppressed, build, savedAt: new Date().toISOString() }),
    );
    setSaved(true);
  };

  return (
    <div className="panel-configurator">
      <div className="panel-product-media">
        <AnimatePresence mode="wait">
          <motion.img
            key={image}
            src={image}
            alt={`VRTS R1 ${finish === "field-earth" ? "Field Earth" : "Carbon"}${suppressed ? " suppressed system" : " rifle"}`}
            initial={{ opacity: 0, scale: 1.015 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28 }}
          />
        </AnimatePresence>
        <div className="panel-build-code">
          <span>BUILD</span>
          <strong>{build}</strong>
        </div>
      </div>

      <div className="panel-config-controls">
        <p className="eyebrow">R1 CONFIGURATOR</p>
        <h2 id="experience-panel-title">Configure your R1.</h2>
        <p>Choose a finish study and system state. The rifle preview updates immediately.</p>

        <fieldset className="choice-group">
          <legend>Finish</legend>
          <button type="button" aria-pressed={finish === "carbon"} onClick={() => { setFinish("carbon"); setSaved(false); }}>
            <span className="finish-swatch carbon" /> Carbon
          </button>
          <button type="button" aria-pressed={finish === "field-earth"} onClick={() => { setFinish("field-earth"); setSaved(false); }}>
            <span className="finish-swatch field-earth" /> Field Earth
          </button>
        </fieldset>

        <fieldset className="choice-group">
          <legend>System</legend>
          <button type="button" aria-pressed={!suppressed} onClick={() => { setSuppressed(false); setSaved(false); }}>
            R1 rifle
          </button>
          <button type="button" aria-pressed={suppressed} onClick={() => { setSuppressed(true); setSaved(false); }}>
            Suppressed system
          </button>
        </fieldset>

        <div className="panel-action-row">
          <button className="button button-primary" type="button" onClick={saveBuild}>
            {saved ? "Build saved" : "Save build"} <Arrow />
          </button>
          <button className="text-link panel-text-button" type="button" onClick={() => onRequestInterest("r1-configurator", build)}>
            Request build access <Arrow />
          </button>
        </div>
        {saved && <p className="panel-confirmation" role="status">Saved to this browser. Build reference {build}.</p>}
      </div>
    </div>
  );
}

function ProductPanel({ product, onRequestInterest }: { product: ProductInfo } & Pick<ExperiencePanelProps, "onRequestInterest">) {
  return (
    <div className="panel-product-layout">
      <div className="panel-product-media">
        <img src={product.image} alt={product.alt} />
      </div>
      <div className="panel-product-copy">
        <p className="eyebrow">{product.status}</p>
        <h2 id="experience-panel-title">{product.name}</h2>
        <p>{product.description}</p>
        <dl className="product-facts">
          <div>
            <dt>Product</dt>
            <dd>{product.detail}</dd>
          </div>
          <div>
            <dt>Availability</dt>
            <dd>{product.status}</dd>
          </div>
        </dl>
        <button className="button button-primary" type="button" onClick={() => onRequestInterest(product.id)}>
          Request availability <Arrow />
        </button>
      </div>
    </div>
  );
}

function OwnershipPanel({ onRequestInterest }: Pick<ExperiencePanelProps, "onRequestInterest">) {
  return (
    <div className="panel-product-layout ownership-panel-layout">
      <div className="panel-product-media">
        <img src="/media/r1-receiver-detail.webp" alt="VRTS serialized R1 receiver detail" />
      </div>
      <div className="panel-product-copy">
        <p className="eyebrow">PROOF OF ORIGIN</p>
        <h2 id="experience-panel-title">The record behind the rifle.</h2>
        <p>Each Founder rifle connects its serial to the build record, inspection history, ownership registration, and future service.</p>
        <dl className="product-facts">
          <div>
            <dt>Build record</dt>
            <dd>Configuration and inspection history</dd>
          </div>
          <div>
            <dt>Owner access</dt>
            <dd>Registration, service, and compatible releases</dd>
          </div>
        </dl>
        <button className="button button-primary" type="button" onClick={() => onRequestInterest("proof-of-origin")}>
          Request ownership brief <Arrow />
        </button>
      </div>
    </div>
  );
}

function InterestForm({ source, build }: { source: string; build?: string }) {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [reference, setReference] = useState("");

  const title = source === "founder"
    ? "Request Founder access."
    : source === "circle"
      ? "Join VRTS Circle."
      : source === "r1-configurator"
        ? "Request build access."
        : "Request release access.";

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("sending");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const response = await fetch("/api/interest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.get("name"),
          email: form.get("email"),
          consent: form.get("consent") === "on",
          company: form.get("company"),
          source,
          build,
        }),
      });
      const payload = await response.json() as { ok?: boolean; requestId?: string };
      if (!response.ok || !payload.ok) throw new Error("request_failed");
      setReference(payload.requestId ?? "VRTS-REQUEST");
      setStatus("success");
      formElement.reset();
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="interest-success" role="status">
        <p className="eyebrow">REQUEST RECEIVED</p>
        <h2 id="experience-panel-title">You are on the release list.</h2>
        <p>Reference {reference}. VRTS will send the relevant release brief when access opens.</p>
      </div>
    );
  }

  return (
    <div className="interest-layout">
      <div>
        <p className="eyebrow">VRTS RELEASE ACCESS</p>
        <h2 id="experience-panel-title">{title}</h2>
        <p>Share your email to receive the relevant product or allocation brief when access opens.</p>
        {build && <p className="interest-build">Build reference: <strong>{build}</strong></p>}
      </div>
      <form className="interest-form" onSubmit={submit}>
        <label>
          Name <span>Optional</span>
          <input name="name" type="text" autoComplete="name" maxLength={80} />
        </label>
        <label>
          Email
          <input name="email" type="email" autoComplete="email" required maxLength={160} />
        </label>
        <label className="field-trap" aria-hidden="true">
          Company
          <input name="company" type="text" tabIndex={-1} autoComplete="off" />
        </label>
        <label className="consent-row">
          <input name="consent" type="checkbox" required />
          <span>I agree to receive VRTS product and release updates.</span>
        </label>
        <button className="button button-primary" type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Submitting" : "Submit request"} <Arrow />
        </button>
        {status === "error" && <p className="form-error" role="alert">The request needs another attempt.</p>}
      </form>
    </div>
  );
}

export function ExperiencePanel({ panel, onClose, onRequestInterest }: ExperiencePanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const reduceMotion = useReducedMotion();
  const isOpen = panel !== null;

  useEffect(() => {
    if (!isOpen) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const background = Array.from(document.querySelectorAll<HTMLElement>(".site-header, main, .site-footer"));
    background.forEach((element) => { element.inert = true; });
    document.body.classList.add("panel-open");
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKeyDown);
      background.forEach((element) => { element.inert = false; });
      document.body.classList.remove("panel-open");
      returnFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {panel && (
        <motion.div
          className="panel-backdrop"
          role="presentation"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) onClose();
          }}
        >
          <motion.aside
            className="experience-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="experience-panel-title"
            initial={reduceMotion ? false : { x: "4%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "4%", opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="panel-header">
              <span>VRTS / PRODUCT EXPERIENCE</span>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close product experience">
                <span />
                <span />
              </button>
            </div>
            <div className="panel-body">
              {panel.kind === "configure" && <Configurator onRequestInterest={onRequestInterest} />}
              {panel.kind === "product" && <ProductPanel product={panel.product} onRequestInterest={onRequestInterest} />}
              {panel.kind === "ownership" && <OwnershipPanel onRequestInterest={onRequestInterest} />}
              {panel.kind === "interest" && <InterestForm source={panel.source} build={panel.build} />}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
